import { MIN_PASSWORD_LENGTH, type PasswordStrengthResult } from 'shared';
import { ApiClientError } from '@/lib/api';

/**
 * Centralized error → European Portuguese (pt-PT) translators for every
 * user-facing error surface in the PWA.
 *
 * The contract (see CLAUDE.md): the Go API speaks English (stable `error`
 * codes, log messages); the PWA is responsible for translating each error into
 * pt-PT before it reaches the user. Every translator below maps known HTTP
 * statuses / stable `error` codes to pt-PT strings and ALWAYS ends in a generic
 * pt-PT fallback — none of them ever return the raw API `err.message` /
 * `body.message`.
 */

/** Structured shape the API returns alongside non-2xx responses. */
interface ApiErrorBody {
  error?: string;
  message?: string;
  code?: string;
  reason?: string;
  errors?: Record<string, string>;
  openingTime?: string;
  closingTime?: string;
  startsAt?: string;
}

/**
 * Safely narrow an `ApiClientError.body` to the structured fields callers match
 * on. Returns null when the error isn't an ApiClientError or carries no object
 * body, so translators can fall straight through to their generic fallback.
 */
function errorBody(err: unknown): ApiErrorBody | null {
  if (!(err instanceof ApiClientError)) return null;
  if (err.body && typeof err.body === 'object') {
    return err.body as ApiErrorBody;
  }
  return null;
}

/**
 * POST /api/centros/check-in. A check-in outside the centre's operating hours
 * returns 422 with the stable code `CentroClosed` plus `openingTime` /
 * `closingTime` ("HH:MM"). A missing/unrecognised QR returns a plain 422 (the
 * API's "qrCode or digitalCenterId is required"); an unknown centre returns
 * 404. Both mean the same thing to the participant: the scanned code isn't a
 * valid Centro QR. 409 AlreadyCheckedIn is handled separately via
 * `isAlreadyCheckedIn()` and never reaches this translator.
 */
export function checkInErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    const body = errorBody(err);
    if (body?.error === 'CentroClosed') {
      if (body.openingTime && body.closingTime) {
        return `O Centro está encerrado. Horário: ${body.openingTime}–${body.closingTime}.`;
      }
      return 'O Centro está encerrado. Tente novamente dentro do horário de funcionamento.';
    }
    if (err.status === 422 || err.status === 404) {
      return 'QR de Centro não reconhecido. Aponte a câmara para o código QR do Centro.';
    }
    if (err.status === 401) {
      return 'A sua sessão expirou. Inicie sessão novamente.';
    }
  }
  return 'Não foi possível fazer check-in. Tente novamente.';
}

/**
 * Shown when a scanned or opened code is not an activity session link at all,
 * before any request reaches the backend.
 */
export const SESSION_QR_NOT_RECOGNISED_MESSAGE =
  'Código de atividade não reconhecido. Leia o código QR mostrado na atividade.';

/**
 * Join an activity session (POST /api/activity-sessions/join). `err.body`
 * carries stable codes:
 *   404 → { error: "SessionNotFound" }
 *   422 → { error: "SessionNotStarted", startsAt } | { error: "SessionEnded" }
 *   403 → { error: "RemovedFromSession" }
 */
export function joinSessionErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    const body = errorBody(err);

    if (body?.error === 'SessionNotStarted') {
      const startsAt = body.startsAt ? new Date(body.startsAt) : null;
      if (startsAt && !Number.isNaN(startsAt.getTime())) {
        const dia = startsAt.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
        const hora = startsAt.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
        return `Esta atividade ainda não começou. Começa a ${dia} às ${hora}.`;
      }
      return 'Esta atividade ainda não começou.';
    }
    if (body?.error === 'SessionEnded') {
      return 'Esta atividade já terminou.';
    }
    if (body?.error === 'RemovedFromSession') {
      return 'A sua presença nesta atividade foi retirada. Fale com quem a dinamiza.';
    }
    if (err.status === 404) {
      return SESSION_QR_NOT_RECOGNISED_MESSAGE;
    }
    if (err.status === 401) {
      return 'A sua sessão expirou. Inicie sessão novamente.';
    }
  }
  return 'Não foi possível entrar na atividade. Tente novamente.';
}

/** POST /api/centros/check-out. */
export function checkOutErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError && err.status === 404) {
    return 'Não há nenhuma visita ativa para terminar.';
  }
  return 'Não foi possível terminar a visita. Tente novamente.';
}

/**
 * Login (participant + staff). Pendente accounts get dedicated copy so a
 * newly registered user understands why they cannot sign in; every other failure stays
 * opaque to avoid leaking whether an account exists.
 */
export function loginErrorMessage(err: unknown): string {
  const body = errorBody(err);
  if (body?.code === 'PENDING_APPROVAL') {
    return 'Conta pendente de aprovação. Aguarde a aprovação por um administrador.';
  }
  if (err instanceof ApiClientError) {
    if (err.status === 429) {
      return 'Conta temporariamente bloqueada por demasiadas tentativas. Tente novamente mais tarde.';
    }
    if (err.status === 401) {
      return 'ID, email ou palavra-passe inválidos.';
    }
  }
  return 'Não foi possível iniciar sessão. Tente novamente.';
}

/** Full password policy, used when we cannot tell which requirement failed. */
export const PASSWORD_REQUIREMENTS_MESSAGE = `A palavra-passe deve ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres e incluir uma letra maiúscula, uma letra minúscula e um número.`;

/**
 * Compose a pt-PT message naming exactly which password requirements failed
 * (see `validatePasswordStrength` in the shared package). Returns null when
 * the password meets every requirement.
 */
export function passwordRequirementsMessage(result: PasswordStrengthResult): string | null {
  if (result.valid) return null;

  const missing: string[] = [];
  if (result.missingUpper) missing.push('uma letra maiúscula');
  if (result.missingLower) missing.push('uma letra minúscula');
  if (result.missingNumber) missing.push('um número');

  const lista =
    missing.length > 1
      ? `${missing.slice(0, -1).join(', ')} e ${missing[missing.length - 1]}`
      : missing[0];

  if (result.tooShort && lista) {
    return `A palavra-passe deve ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres e incluir ${lista}.`;
  }
  if (result.tooShort) {
    return `A palavra-passe deve ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`;
  }
  return `A palavra-passe deve incluir ${lista}.`;
}

/**
 * Participant registration. The API answers validation failures with 400 and a
 * distinct English message per field (see ParticipantAuthHandler.Register), so
 * the 400 branch matches on those known messages to keep the pt-PT copy
 * specific to the field that failed instead of one generic sentence.
 */
export function registerErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.status === 409) {
      return 'Já existe uma conta com este email.';
    }
    if (err.status === 429) {
      return 'Demasiadas tentativas. Tente novamente mais tarde.';
    }
    if (err.status === 400) {
      const message = errorBody(err)?.message ?? '';
      if (message.includes('Password')) {
        return PASSWORD_REQUIREMENTS_MESSAGE;
      }
      if (message.includes('email address format')) {
        return 'O formato do email é inválido.';
      }
      if (message.includes('Email or phone number')) {
        return 'Indique um email ou telemóvel para poder recuperar a sua palavra-passe.';
      }
      if (message.includes('Date of birth')) {
        return 'A data de nascimento deve estar no formato AAAA-MM-DD.';
      }
      if (message.includes('CAPTCHA')) {
        return 'A verificação captcha falhou. Tente novamente.';
      }
      if (message.includes('First name')) {
        return 'O nome é obrigatório.';
      }
      if (message.includes('Last name')) {
        return 'O apelido é obrigatório.';
      }
      if (message.includes('Gender')) {
        return 'Selecione o género.';
      }
      return 'Não foi possível criar a conta. Verifique os dados introduzidos.';
    }
  }
  return 'Não foi possível criar a conta. Tente novamente.';
}

/**
 * Submit a new password with a reset token. The API uses 400 both for a weak
 * password and for a bad token, so tell them apart by the known English
 * message — a weak password must not be reported as an expired link.
 */
export function resetPasswordErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError && err.status === 400) {
    const message = errorBody(err)?.message ?? '';
    if (message.includes('Password must contain')) {
      return PASSWORD_REQUIREMENTS_MESSAGE;
    }
    return 'A ligação de recuperação é inválida ou expirou. Peça uma nova.';
  }
  return 'Não foi possível redefinir a palavra-passe. Tente novamente.';
}

/**
 * Client-side scan guard (PWA only): the scanned equipment QR is well-formed
 * but the equipment does not belong to the Centro the participant is checked
 * into. Surfaced before any request reaches the backend.
 */
export const EQUIPMENT_NOT_IN_CENTRO_MESSAGE =
  'Este equipamento não pertence a este Centro.';

/**
 * Check-out is blocked while the participant still holds equipment in use.
 * Equipment must no longer be in use before the visit can be terminated.
 */
export const RETURN_EQUIPMENT_BEFORE_CHECKOUT_MESSAGE =
  'Tem equipamentos em utilização. Para terminar a visita não pode ter equipamentos em utilização.';

/**
 * The scanned equipment requires a badge/certification the participant does not
 * hold (active). Surfaced both by the PWA pre-check and by the backend guard
 * (403 BadgeRequired). The exact wording is mandated by the product spec.
 */
export const CERTIFICATION_REQUIRED_MESSAGE =
  'Utilizador não possui a certificação necessária para a utilização deste equipamento';

/**
 * Equipment scan (POST /.../scan). `err.body` carries stable codes:
 *   404 → { error: "EquipmentNotFound" }
 *   409 → { error: "NotOwner" }
 *   403 → { error: "BadgeRequired" }
 *   422 → { error: "EquipmentUnavailable", reason: "..." }
 *   422 (validation) → { errors: { equipmentId: "..." } }
 */
export function scanErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    const body = errorBody(err);

    if (err.status === 404) {
      return 'Equipamento não encontrado.';
    }
    if (err.status === 403 && body?.error === 'BadgeRequired') {
      return CERTIFICATION_REQUIRED_MESSAGE;
    }
    if (err.status === 409 && body?.error === 'NotOwner') {
      return 'Equipamento em uso por outro participante.';
    }
    if (err.status === 422 && body?.error === 'EquipmentUnavailable') {
      return 'Equipamento indisponível.';
    }
    if (err.status === 422 && body?.errors) {
      const first = Object.values(body.errors)[0];
      if (first) return first;
    }
  }
  return 'Não foi possível processar o scan. Tente novamente.';
}
